'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Flame, Snowflake, Wind, Trash2, BarChart3,
  ClipboardList, Map, Search, HelpCircle, Brain,
  PackageOpen, RefreshCw, Zap, Download, Bookmark,
  Code, FileText, Terminal, Settings, Target,
} from 'lucide-react';

export interface Command {
  name: string;
  description: string;
  usage: string;
  icon: React.ElementType;
  tier: 'gateway' | 'extension' | 'ui';
}

const COMMANDS: Command[] = [
  // Soma commands — all go through session.prompt() → Pi SDK extensions handle them
  { name: '/exhale', description: 'Save state — preload, session log, kanban', usage: '/exhale', icon: Wind, tier: 'extension' },
  { name: '/inhale', description: 'Load preload + resume', usage: '/inhale [name]', icon: Brain, tier: 'extension' },
  { name: '/breathe', description: 'Save state and auto-rotate to fresh session', usage: '/breathe', icon: Wind, tier: 'extension' },
  { name: '/auto-breathe', description: 'Toggle proactive context rotation', usage: '/auto-breathe [on|off|status]', icon: Wind, tier: 'extension' },
  { name: '/status', description: 'Show keepalive, context, session stats', usage: '/status', icon: BarChart3, tier: 'extension' },
  { name: '/keepalive', description: 'Toggle cache keepalive', usage: '/keepalive [on|off|status]', icon: Zap, tier: 'extension' },
  { name: '/guard-status', description: 'Show file protection rules', usage: '/guard-status', icon: Search, tier: 'extension' },
  { name: '/hub', description: 'Community hub — install, share, browse', usage: '/hub [install|share|list]', icon: Map, tier: 'extension' },
  { name: '/soul-space', description: 'Toggle soul-space introspection', usage: '/soul-space [on|off|status]', icon: Brain, tier: 'extension' },
  { name: '/rest', description: 'Disable keepalive and exhale', usage: '/rest', icon: Wind, tier: 'extension' },
  { name: '/scratch', description: 'Private notes — append, read, or clear', usage: '/scratch <note>|read|clear', icon: Bookmark, tier: 'extension' },
  { name: '/pin', description: 'Bump protocol/muscle heat', usage: '/pin <name>', icon: Flame, tier: 'extension' },
  { name: '/kill', description: 'Drop heat to 0', usage: '/kill <name>', icon: Snowflake, tier: 'extension' },
  { name: '/focus', description: 'Prime session for a specific topic', usage: '/focus <keyword>', icon: Target, tier: 'extension' },
  { name: '/body', description: 'Inspect identity templates', usage: '/body [check|vars|map|render]', icon: FileText, tier: 'extension' },
  { name: '/code', description: 'Codebase navigator — map, find, trace', usage: '/code <subcommand> [args]', icon: Code, tier: 'extension' },
  { name: '/soma', description: 'Soma status, prompt, debug, preload', usage: '/soma [status|prompt|debug|preload]', icon: Brain, tier: 'extension' },
  { name: '/model', description: 'Switch AI model', usage: '/model', icon: Zap, tier: 'extension' },
  { name: '/auto-commit', description: 'Toggle auto-commit on exhale', usage: '/auto-commit [on|off]', icon: Settings, tier: 'extension' },

  // SDK builtins — session-level operations
  { name: '/compact', description: 'Manually compact session context', usage: '/compact', icon: PackageOpen, tier: 'gateway' },
  { name: '/reload', description: 'Reload extensions, skills, prompts', usage: '/reload', icon: RefreshCw, tier: 'gateway' },
  { name: '/export', description: 'Export session as HTML or JSONL', usage: '/export [html|jsonl]', icon: Download, tier: 'gateway' },

  // UI-only commands
  { name: '/usage', description: 'Fetch Claude Max plan usage', usage: '/usage', icon: BarChart3, tier: 'ui' },
  { name: '/clear', description: 'Clear this chat (keep session)', usage: '/clear', icon: Trash2, tier: 'ui' },
  { name: '/new', description: 'New chat — clear history + reset session', usage: '/new', icon: Trash2, tier: 'ui' },
  { name: '/help', description: 'Show all available commands', usage: '/help', icon: HelpCircle, tier: 'ui' },
];

interface CommandPaletteProps {
  input: string;
  visible: boolean;
  selectedIndex: number;
  onSelect: (command: string) => void;
  filteredCommands: Command[];
}

export function getFilteredCommands(input: string): Command[] {
  if (!input.startsWith('/')) return [];
  const query = input.toLowerCase();
  if (query === '/') return COMMANDS;
  return COMMANDS.filter(c =>
    c.name.startsWith(query) || c.description.toLowerCase().includes(query.slice(1))
  );
}

export function CommandPalette({ input, visible, selectedIndex, onSelect, filteredCommands }: CommandPaletteProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[selectedIndex] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!visible || filteredCommands.length === 0) return null;

  return (
    <div
      ref={listRef}
      className="absolute bottom-full left-0 right-0 mb-2 mx-0 bg-gray-900 border border-[rgb(var(--border))]/60 rounded-xl shadow-2xl shadow-black/40 overflow-hidden max-h-[300px] overflow-y-auto z-50"
    >
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 border-b border-[rgb(var(--border))]/60">
        Commands
      </div>
      {filteredCommands.map((cmd, i) => {
        const Icon = cmd.icon;
        return (
          <button
            key={cmd.name}
            onClick={() => onSelect(cmd.name.includes('<') ? cmd.name.split(' ')[0] + ' ' : cmd.name)}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
              i === selectedIndex
                ? 'bg-cyan-500/10 text-cyan-400'
                : 'text-gray-300 hover:bg-white/5'
            )}
          >
            <Icon className={cn(
              'w-4 h-4 flex-shrink-0',
              i === selectedIndex ? 'text-cyan-400' : 'text-gray-500'
            )} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono font-medium">{cmd.name}</span>
                {cmd.usage !== cmd.name && (
                  <span className="text-[10px] text-gray-600 font-mono">{cmd.usage}</span>
                )}
              </div>
              <div className="text-[11px] text-gray-500 truncate">{cmd.description}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
